import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/give", label: "Give" },
  { to: "/adopt", label: "Adopt & Volunteer" },
  { to: "/stories", label: "Stories" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  const isHome = pathname === "/";
  const isDonate = pathname === "/donate";
  const transparent = (isHome || isDonate) && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-transparent border-b border-transparent"
          : "bg-bone/95 backdrop-blur-md border-b border-bone-soft"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="nav-logo-link">
          <img
            src="/logo-horizontal.png"
            alt="Pawsomeland Foundation"
            className={`h-12 lg:h-14 w-auto transition-all group-hover:scale-[1.03] ${transparent ? "brightness-0 invert" : ""}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}-link`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors ${
                  transparent
                    ? isActive ? "text-honey" : "text-bone hover:text-honey"
                    : isActive ? "text-terracotta" : "text-forest hover:text-terracotta"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex">
          <Link to="/donate" data-testid="nav-donate-cta">
            <Button className="bg-terracotta hover:bg-terracotta-deep text-bone rounded-full px-7 h-11 font-semibold tracking-wide shadow-[0_8px_20px_rgba(200,90,64,0.35)]">
              Donate Now
            </Button>
          </Link>
        </div>

        <button
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors ${transparent ? "text-bone hover:bg-bone/15" : "text-forest hover:bg-bone-soft"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-bone-soft bg-bone" data-testid="mobile-menu">
          <div className="flex flex-col p-6 gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}-link`}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-terracotta" : "text-forest"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/donate" onClick={() => setOpen(false)} data-testid="mobile-nav-donate-cta">
              <Button className="w-full bg-terracotta hover:bg-terracotta-deep text-bone rounded-full">
                Donate Now
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
